// Page component for /personal-information (first step)
import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FormInput from "../../questionnaire/components/FormInput";
import FormSelect from "../../questionnaire/components/FormSelect";
import AppButton from "../../questionnaire/components/AppButton";
import CheckinLayout from "../../questionnaire/components/CheckinLayout";
import { useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useDemographics } from "../../questionnaire/contexts/DemographicsContext";
import { useMasterData } from "../../questionnaire/contexts/MasterDataContext";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import { useLoader } from "../../questionnaire/contexts/LoaderContext";
import { mapValueToMasterValue, type ValueField } from "../../questionnaire/utils/masterData";
import { months, sexOptions } from "../../questionnaire/questionnaire_constants";
import { Alert } from "../../components/shared/Alert";
const maritalStatusPlaceholder = { label: "Select Option", value: "" };
// const occupationPlaceholder = { label: "Select Occupation", value: "" };
// const ethnicityPlaceholder = { label: "Select Ethnicity", value: "" };
// const racePlaceholder = { label: "Select Race", value: "" };
// const languagePlaceholder = { label: "Select Language", value: "" };

function parseDob(dob?: string | null): { day: string; month: string; year: string } {
  if (!dob) {
    return { day: "", month: "", year: "" };
  }
  const parts = dob.split("/");
  if (parts.length !== 3) {
    return { day: "", month: "", year: "" };
  }
  const [rawMonth, rawDay, year] = parts;
  const day = rawDay || "";
  const month = rawMonth ? String(parseInt(rawMonth, 10)) : "";
  return {
    day,
    month,
    year: year || "",
  };
}

const PersonalInformation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { demographics, loading, error, updateDemographics } = useDemographics();
  const { data: masterData } = useMasterData();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError, loading: workflowLoading, refreshWorkflow } = useWorkflow();
  const { show, hide, withLoader } = useLoader();

  const personalPath = useAppointmentStepPath("personal-information");
  const contactPath = useAppointmentStepPath("contact-and-communication");

  const [submitting, setSubmitting] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const inFlightSubmitRef = React.useRef(false);

  React.useEffect(() => {
    // Hide the loader as soon as an error is reported — otherwise the
    // overlay covers the error Alert and the user sees an infinite spinner
    // (e.g. PAST_APPOINTMENT keeps `loading=false` but `demographics=null`).
    const hasError = !!(error || workflowError);
    const initialLoading = (workflowLoading || loading || !demographics || !masterData) && !hasError;
    if (initialLoading) show("Loading checkin...");
    else hide();
    return () => hide();
  }, [workflowLoading, loading, demographics, masterData, error, workflowError, show, hide]);

  // Store initial state for change detection
  const [initialState, setInitialState] = React.useState<{
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    sex: string;
    genderIdentity: string;
    sexualOrientation: string;
    preferredPronouns: string;
    maritalStatus: string;
    // occupation: string;
    // ethnicity: string;
    // race: string;
    // language: string;
  } | null>(null);

  // Controlled form state
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [dobDay, setDobDay] = React.useState("");
  const [dobMonth, setDobMonth] = React.useState("");
  const [dobYear, setDobYear] = React.useState("");
  const [sex, setSex] = React.useState("");

  const [genderIdentity, setGenderIdentity] = React.useState("");
  const [sexualOrientation, setSexualOrientation] = React.useState("");
  const [preferredPronouns, setPreferredPronouns] = React.useState("");
  const [maritalStatus, setMaritalStatus] = React.useState("");
  // const [occupation, setOccupation] = React.useState("");
  // const [ethnicity, setEthnicity] = React.useState("");
  // const [race, setRace] = React.useState("");
  // const [language, setLanguage] = React.useState("");

  // Per-field master-data value field (makes it easy to swap what we use for select values)
  const genderIdentityValueField: ValueField = "name";
  const sexualOrientationValueField: ValueField = "name";
  const preferredPronounsValueField: ValueField = "name";
  const maritalStatusValueField: ValueField = "code";
  // const occupationValueField: ValueField = "code";
  // const ethnicityValueField: ValueField = "code";
  // const raceValueField: ValueField = "raceid";
  // const languageValueField: ValueField = "code";

  // Initialize state from demographics + master data
  React.useEffect(() => {
    if (!demographics || !masterData) {
      return;
    }

    const parsedDob = parseDob(demographics.dateOfBirth || null);
    const initialFirstName = demographics.firstName || "";
    const initialLastName = demographics.lastName || "";
    const initialDob = demographics.dateOfBirth || "";
    const initialSex = demographics.sex || "";

    const initialGenderIdentity = mapValueToMasterValue(demographics.genderIdentity, masterData.genderIdentity, genderIdentityValueField);
    const initialSexualOrientation = mapValueToMasterValue(demographics.sexualOrientation, masterData.sexualOrientation, sexualOrientationValueField);
    const initialPreferredPronouns = mapValueToMasterValue(demographics.preferredPronouns, masterData.preferredPronouns, preferredPronounsValueField);
    const initialMaritalStatus = mapValueToMasterValue(demographics.maritalStatus, masterData.maritalStatus, maritalStatusValueField);
    // const initialOccupation = mapValueToMasterValue(demographics.occupation, masterData.occupation, occupationValueField);
    // const initialEthnicity = mapValueToMasterValue(demographics.ethnicity, masterData.ethnicity, ethnicityValueField);
    // const initialRace = mapValueToMasterValue(demographics.race, masterData.race, raceValueField);
    // const initialLanguage = mapValueToMasterValue(demographics.language, masterData.language, languageValueField);

    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setDobDay(parsedDob.day);
    setDobMonth(parsedDob.month);
    setDobYear(parsedDob.year);
    setSex(initialSex);
    setGenderIdentity(initialGenderIdentity);
    setSexualOrientation(initialSexualOrientation);
    setPreferredPronouns(initialPreferredPronouns);
    setMaritalStatus(initialMaritalStatus);
    // setOccupation(initialOccupation);
    // setEthnicity(initialEthnicity);
    // setRace(initialRace);
    // setLanguage(initialLanguage);

    // Store initial state for change detection
    setInitialState({
      firstName: initialFirstName,
      lastName: initialLastName,
      dateOfBirth: initialDob,
      sex: initialSex,
      genderIdentity: initialGenderIdentity,
      sexualOrientation: initialSexualOrientation,
      preferredPronouns: initialPreferredPronouns,
      maritalStatus: initialMaritalStatus,
      // occupation: initialOccupation,
      // ethnicity: initialEthnicity,
      // race: initialRace,
      // language: initialLanguage,
    });
  }, [demographics, masterData]);

  if (error && !demographics) {
    return (
      <CheckinLayout showProgress={false}>
        <Alert type="error" className="mb-4">
          {error}
        </Alert>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-[#01545D] text-white rounded-md"
        >
          Try Again
        </button>
      </CheckinLayout>
    );
  }

  // Helper function to check if there are any changes
  const hasChanges = (): boolean => {
    if (!initialState) return true; // If no initial state, assume there are changes

    const currentDateOfBirth = dobDay && dobMonth && dobYear ? `${dobMonth}/${dobDay}/${dobYear}` : "";
    const currentPreferredPronouns = preferredPronouns ? preferredPronouns.toLowerCase() : "";

    return (
      firstName !== initialState.firstName ||
      lastName !== initialState.lastName ||
      currentDateOfBirth !== initialState.dateOfBirth ||
      sex !== initialState.sex ||
      genderIdentity !== initialState.genderIdentity ||
      sexualOrientation !== initialState.sexualOrientation ||
      currentPreferredPronouns !== (initialState.preferredPronouns ? initialState.preferredPronouns.toLowerCase() : "") ||
      maritalStatus !== initialState.maritalStatus
      // occupation !== initialState.occupation ||
      // ethnicity !== initialState.ethnicity
      // race !== initialState.race ||
      // language !== initialState.language
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightSubmitRef.current) return;
    inFlightSubmitRef.current = true;
    try {
    // Check if there are any changes before calling the API
    if (!hasChanges()) {
      // No changes detected, just navigate to next page
      navigate(contactPath);
      return;
    }

    const payload = {
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      dateOfBirth:
        dobDay && dobMonth && dobYear ? `${dobMonth}/${dobDay}/${dobYear}` : undefined,
      sex: (sex || undefined) as string | undefined,
      genderIdentity: genderIdentity || undefined,
      sexualOrientation: sexualOrientation || undefined,
      preferredPronouns: preferredPronouns ? preferredPronouns.toLowerCase() : undefined,
      maritalStatus: maritalStatus || undefined,
      // occupation: occupation || undefined,
      // ethnicity: ethnicity || undefined,
      // race: race || undefined,
      // language: language || undefined,
      firstNameUsed: firstName || undefined,
    };

    setSubmitting(true);
    try {
      await withLoader("Saving...", async () => {
        await updateDemographics(payload);
        // Refetch workflow so the progress bar reflects backend truth
        // (step statuses, newly-completed steps) before we navigate.
        await refreshWorkflow();
        if (isAllStepsCompleted()) {
          navigate(`/appointments/${appointmentId}/complete`);
        } else {
          const nextStep = getNextStepInSequence(location.pathname);
          if (nextStep) {
            navigate(nextStep.stepUrl);
          } else {
            navigate(contactPath);
          }
        }
      });
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
    } finally {
      inFlightSubmitRef.current = false;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveAndExit = async () => {
    // Check if there are any changes before calling the API
    if (initialState && !hasChanges()) {
      // No changes detected, navigate to next pending step or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(personalPath.replace("personal-information", "payments"));
        }
      }
      return;
    }

    const payload = {
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      dateOfBirth:
        dobDay && dobMonth && dobYear ? `${dobMonth}/${dobDay}/${dobYear}` : undefined,
      sex: (sex || undefined) as string | undefined,
      genderIdentity: genderIdentity || undefined,
      sexualOrientation: sexualOrientation || undefined,
      preferredPronouns: preferredPronouns ? preferredPronouns.toLowerCase() : undefined,
      maritalStatus: maritalStatus || undefined,
      // occupation: occupation || undefined,
      // ethnicity: ethnicity || undefined,
      // race: race || undefined,
      // language: language || undefined,
      firstNameUsed: firstName || undefined,
    };

    setSubmitting(true);
    try {
      await updateDemographics(payload);
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(personalPath.replace("personal-information", "payments"));
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CheckinLayout
      title="Personal Information"
      subtitle="Please provide your personal information to complete your booking. All fields marked with * are required."
    >
        {(error || localError || workflowError) && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert
              type="error"
              onClose={() => {
                setLocalError(null);
              }}
            >
              {localError || workflowError || error}
            </Alert>
          </div>
        )}
        <form
          className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormInput
              label="First Name"
              name="first_name"
              placeholder="Value"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <FormInput
              label="Last Name"
              name="last_name"
              placeholder="Value"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />

            <div className="flex flex-col gap-2">
              <label className="font-semibold text-[#1C1C1C] text-base">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="number"
                    name="dob_day"
                    placeholder="DD"
                    min={1}
                    max={31}
                    required
                    value={dobDay}
                    onChange={(e) => setDobDay(e.target.value)}
                    className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                  />
                </div>
                <div className="flex-1">
                  <FormSelect
                    label=""
                    name="dob_month"
                    options={months}
                    required
                    className="w-full"
                    value={dobMonth}
                    onChange={(e) => setDobMonth(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="number"
                    name="dob_year"
                    placeholder="YYYY"
                    min={1900}
                    max={new Date().getFullYear()}
                    required
                    value={dobYear}
                    onChange={(e) => setDobYear(e.target.value)}
                    className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                  />
                </div>
              </div>
            </div>

            <FormSelect
              label="Sex"
              name="sex"
              options={sexOptions}
              required
              value={sex}
              onChange={(e) => setSex(e.target.value)}
            />
            <FormSelect
              label="Gender Identity"
              name="gender_identity"
              options={[
                { label: "Select Gender Identity", value: "" },
                ...(masterData?.genderIdentity?.map((g) => ({
                  label: g.name,
                  value: g[genderIdentityValueField],
                })) || []),
              ]}
              value={genderIdentity}
              onChange={(e) => setGenderIdentity(e.target.value)}
            />
            <FormSelect
               label="Sexual Orientation"
               name="sexual_orientation"
               options={[
                 { label: "Select Sexual Orientation", value: "" },
                 ...(masterData?.sexualOrientation?.map((s) => ({
                   label: s.name,
                   value: s[sexualOrientationValueField],
                 })) || []),
               ]}
               value={sexualOrientation}
               onChange={(e) => setSexualOrientation(e.target.value)}
             />
            <FormSelect
               label="Preferred Pronouns"
               name="pronouns"
               options={[
                 { label: "Select Preferred Pronouns", value: "" },
                 ...(masterData?.preferredPronouns?.map((p) => ({
                   label: p.name,
                   value: p[preferredPronounsValueField],
                 })) || []),
               ]}
               value={preferredPronouns}
               onChange={(e) => setPreferredPronouns(e.target.value)}
             />
            <FormSelect
               label="Marital Status"
               name="marital_status"
               options={[
                 maritalStatusPlaceholder,
                 ...(masterData?.maritalStatus?.map((m) => ({
                   label: m.name,
                   value: m[maritalStatusValueField],
                 })) || []),
               ]}
               value={maritalStatus}
               onChange={(e) => setMaritalStatus(e.target.value)}
             />
            {/* <FormSelect
               label="Occupation"
               name="occupation"
               options={[
                 occupationPlaceholder,
                 ...(masterData?.occupation?.map((o) => ({
                   label: o.name,
                   value: o[occupationValueField],
                 })) || []),
               ]}
               value={occupation}
               onChange={(e) => setOccupation(e.target.value)}
             /> */}
            {/* <FormSelect
               label="Ethnicity"
               name="ethnicity"
               options={[
                 ethnicityPlaceholder,
                 ...(masterData?.ethnicity?.map((e) => ({
                   label: e.name,
                   value: e[ethnicityValueField],
                 })) || []),
               ]}
               value={ethnicity}
               onChange={(e) => setEthnicity(e.target.value)}
             /> */}
            {/* <FormSelect
               label="Race"
               name="race"
               options={[
                 racePlaceholder,
                 ...(masterData?.race?.map((r) => ({
                   label: r.name,
                   value: r[raceValueField],
                 })) || []),
               ]}
               value={race}
               onChange={(e) => setRace(e.target.value)}
             /> */}
            {/* <FormSelect
               label="Preferred Language"
               name="language"
               options={[
                 languagePlaceholder,
                 ...(masterData?.language?.map((l) => ({
                   label: l.name,
                   value: l[languageValueField],
                 })) || []),
               ]}
               // required
               value={language}
               onChange={(e) => setLanguage(e.target.value)}
             /> */}
          </div>
          <div className="text-base text-[#1C1C1C] mt-2">
            All fields marked with <span className="text-red-500">*</span> are required
          </div>
          <div className="flex flex-col items-center gap-2 mt-8">
            <div className="flex gap-5 w-full">
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => {
                  const previousStep = getPreviousStep(location.pathname);
                  if (previousStep) {
                    navigate(previousStep.stepUrl);
                  } else {
                    // On first step, stay on same page or navigate to personal path
                    navigate(personalPath);
                  }
                }}
                disabled={!!workflowError || submitting || loading}
                className="flex-1"
              >
                Back
              </AppButton>
              {/* <AppButton
                variant="secondary"
                type="button"
                onClick={handleSaveAndExit}
                disabled={submitting || loading}
                className="flex-1"
              >
                {submitting ? "Saving..." : "Save & Exit"}
              </AppButton> */}
              <AppButton type="submit" disabled={!!workflowError || submitting || loading} className="flex-1">
                {submitting ? "Saving..." : "Continue"}
              </AppButton>
            </div>
          </div>
        </form>
    </CheckinLayout>
  );
};

export default PersonalInformation;
